import GUI from "lil-gui";
import * as THREE from "three";
import { gsap } from "gsap";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import dustVertexShader from "./shaders/dust/vertex.glsl";
import dustFragmentShader from "./shaders/dust/fragment.glsl";

/**
 * Base
 */
// Debug
const gui = new GUI({
  width: 400,
});

gui.close();
gui.hide();

if (window.location.hash === "#debug") {
  gui.show();
}

const shaderTweaks = gui.addFolder("Shader");
const cameraTweaks = gui.addFolder("Camera");
cameraTweaks.close();
const dustTweaks = shaderTweaks.addFolder("Dust");
dustTweaks.close();

const debugObject = {};

const loadingBarBackground = document.querySelector(".loading-background");
const loadingBarElement = document.querySelector(".loading-bar");
const percentage = document.querySelector(".percentage");

let sceneReady = false;
const loadingManager = new THREE.LoadingManager(
  // Loaded
  () => {
    // ...
    window.setTimeout(() => {
      loadingBarBackground.classList.add("ended");
      loadingBarBackground.style.transform = "";
      loadingBarElement.classList.add("ended");
      percentage.classList.add("ended");
      loadingBarElement.style.transform = "";
      percentage.style.transform = "";
      window.setTimeout(() => {
        loadingBarBackground.remove();
        loadingBarElement.remove();
        percentage.remove();
      }, 5000);
    }, 500);
    window.setTimeout(() => {
      sceneReady = true;
    }, 3500);
  },
  (itemUrl, itemsLoaded, itemsTotal) => {
    const progressRatio = itemsLoaded / itemsTotal;
    loadingBarElement.style.transform = `scaleX(${progressRatio})`;
    percentage.innerText = (progressRatio * 100).toFixed(0) + " %";
  }

  // ...
);

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Loaders
 */
// Texture loader
const textureLoader = new THREE.TextureLoader(loadingManager);

// Draco loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("draco/");

// GLTF loader
const gltfLoader = new GLTFLoader(loadingManager);
gltfLoader.setDRACOLoader(dracoLoader);

/**
 * Textures
 */
const bakedTexture1 = textureLoader.load("./textures/FullBakedTexture_Final.png");
bakedTexture1.flipY = false;
bakedTexture1.colorSpace = THREE.SRGBColorSpace;

/**
 * Materials
 */

// Baked Material
const material1 = new THREE.MeshBasicMaterial({
  map: bakedTexture1,
});

const MonkeyTexture = textureLoader.load("./textures/MonkeyMap.png");
MonkeyTexture.flipY = false;
const MonkeyEmmision = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: MonkeyTexture,
  emissive: new THREE.Color(0xff17ad), // adjust the color as needed
  emissiveIntensity: 1, // adjust the intensity as needed
  transparent: true,
  alphaMap: MonkeyTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const LiarTexture = textureLoader.load("./textures/LiarMap.png");
LiarTexture.flipY = false;
const LiarEmmision_blue = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: LiarTexture,
  emissive: new THREE.Color(0x28d1ff), // adjust the color as needed
  emissiveIntensity: 1, // adjust the intensity as needed
  transparent: true,
  alphaMap: LiarTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});
const LiarEmmision_pink = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: LiarTexture,
  emissive: new THREE.Color(0xff17ad), // adjust the color as needed
  emissiveIntensity: 1, // adjust the intensity as needed
  transparent: true,
  alphaMap: LiarTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const SkullTexture = textureLoader.load("./textures/SkullMap.png");
SkullTexture.flipY = false;
const SkullEmmision = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: SkullTexture,
  emissive: new THREE.Color(0x75fff1), // adjust the color as needed
  emissiveIntensity: 1, // adjust the intensity as needed
  transparent: true,
  alphaMap: SkullTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const MyloTexture = textureLoader.load("./textures/MyloMap.png");
MyloTexture.flipY = false;
const MyloEmmision = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: MyloTexture,
  emissive: new THREE.Color(0xffffff), // adjust the color as needed
  emissiveIntensity: 1, // adjust the intensity as needed
  transparent: true,
  alphaMap: MyloTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireTexture = textureLoader.load("./textures/FlameMap.png");
FireTexture.flipY = false;
const FireEmmision = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision2 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision3 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision4 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision5 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision6 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision7 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision8 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision9 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision10 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const FireEmmision11 = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  emissiveMap: FireTexture,
  emissive: new THREE.Color(0xfedd6b), // adjust the color as needed
  emissiveIntensity: 5, // adjust the intensity as needed
  transparent: true,
  alphaMap: FireTexture, // use the emission texture's alpha for transparency
  side: THREE.DoubleSide, // ensure visibility from both sides if needed
});

const ShadowMap = textureLoader.load("./textures/ShadowMap.png");
ShadowMap.flipY = false;
const MonkeyShadowMaterial = new THREE.MeshStandardMaterial({
  map: bakedTexture1,
  alphaMap: ShadowMap,
  color: new THREE.Color(0x4287f5),
  transparent: true,
  side: THREE.DoubleSide,
});

let mixer;
let skullGraffitiMesh;

gltfLoader.load("./models/JinxHideout.glb", (gltf) => {
  gltf.scene.traverse((child) => {
    if (child.isMesh) {
      mixer = new THREE.AnimationMixer(gltf.scene);
      const action = mixer.clipAction(gltf.animations[0]);
      action.play();
      switch (child.name) {
        case "Grafitti_1":
          child.material = MonkeyEmmision;
          break;
        case "Grafitti_2":
          child.material = LiarEmmision_blue;
          break;
        case "Grafitti_3":
          child.material = SkullEmmision;
          break;
        case "Grafitti_4":
          child.material = LiarEmmision_pink;
          break;
        case "Skull_Gravitti":
          child.material = MyloEmmision;
          skullGraffitiMesh = child; // Assign the Skull_Gravitti mesh to the variable
          break;
        case "CandleFire1":
          child.material = FireEmmision;
          break;
        case "CandleFire2":
          child.material = FireEmmision2;
          break;
        case "CandleFire3":
          child.material = FireEmmision3;
          break;
        case "CandleFire4":
          child.material = FireEmmision4;
          break;
        case "CandleFire5":
          child.material = FireEmmision5;
          break;
        case "CandleFire6":
          child.material = FireEmmision6;
          break;
        case "CandleFire7":
          child.material = FireEmmision7;
          break;
        case "CandleFire8":
          child.material = FireEmmision8;
          break;
        case "CandleFire9":
          child.material = FireEmmision9;
          break;
        case "CandleFire10":
          child.material = FireEmmision10;
          break;
        case "CandleFire11":
          child.material = FireEmmision11;
          break;
        case "MonkeyShadow":
          child.material = MonkeyShadowMaterial;
          break;
        default:
          child.material = material1;
          break;
      }
    }
  });

  gltf.scene.position.y = -2;

  scene.add(gltf.scene);
});

/**
 * Dust
 */

//Geometry
const dustGeometry = new THREE.BufferGeometry();
const dustCount = 200;
const positionArray = new Float32Array(dustCount * 3);
const scaleArray = new Float32Array(dustCount);

const xOffset = 0;
const yOffset = 2;
const zOffset = 0.0; // Adjust as needed for the z-axis

for (let i = 0; i < dustCount; i++) {
  positionArray[i * 3 + 0] = (Math.random() - 0.5) * 10.0 + xOffset;
  positionArray[i * 3 + 1] = (Math.random() - 0.5) * 8.0 + yOffset;
  positionArray[i * 3 + 2] = (Math.random() - 0.5) * 10.0 + zOffset;

  scaleArray[i] = Math.random();
}

dustGeometry.setAttribute("position", new THREE.BufferAttribute(positionArray, 3));
dustGeometry.setAttribute("aScale", new THREE.BufferAttribute(scaleArray, 1));

//Material
const dustMaterial = new THREE.ShaderMaterial({
  uniforms: {
    uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
    uSize: { value: 60 },
    uTime: { value: 0 },
  },
  transparent: true,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
  vertexShader: dustVertexShader,
  fragmentShader: dustFragmentShader,
});

dustTweaks.add(dustMaterial.uniforms.uSize, "value").min(0).max(500).step(1).name("Dust Size");

//Points
const dust = new THREE.Points(dustGeometry, dustMaterial);
scene.add(dust);

/**
 * POI
 */

const points = [
  {
    position: new THREE.Vector3(-1.4, -0.6, 0.5),
    element: document.querySelector(".point-0"),
  },
  {
    position: new THREE.Vector3(3.6, -1.6, -1),
    element: document.querySelector(".point-1"),
  },
  {
    position: new THREE.Vector3(3.5, 0.8, 5.2),
    element: document.querySelector(".point-2"),
  },
];

debugObject.poi = true;
gui
  .add(debugObject, "poi")
  .onChange((val) => {
    for (const point of points) {
      if (!val) {
        point.element.classList.remove("visible");
      } else {
        point.element.classList.add("visible");
      }
    }
  })
  .name("Points of Interest");

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100);
camera.position.x = 4;
camera.position.y = 17;
camera.position.z = 4;
scene.add(camera);

cameraTweaks.add(camera.position, "y").min(0).max(20).step(1).name("Camera Height");
cameraTweaks.add(camera.position, "x").min(-10).max(10).step(1).name("Camera Width");
cameraTweaks.add(camera.position, "z").min(-10).max(10).step(1).name("Camera Depth");

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.minDistance = 16;
// //set zoom to 15
controls.maxDistance = 25;

controls.minAzimuthAngle = -Math.PI / 10;
controls.maxAzimuthAngle = Math.PI / 1.7;

controls.minPolarAngle = Math.PI / 2.7;
controls.maxPolarAngle = Math.PI / 2.7;

cameraTweaks.add(controls, "minAzimuthAngle").min(0).max(1.6).step(0.01).name("Min Azimuth Angle");
cameraTweaks.add(controls, "maxAzimuthAngle").min(0).max(1.6).step(0.01).name("Max Azimuth Angle");
cameraTweaks.add(controls, "minPolarAngle").min(0).max(1.6).step(0.01).name("Min Polar Angle");
cameraTweaks.add(controls, "maxPolarAngle").min(0).max(1.6).step(0.01).name("Max Polar Angle");
cameraTweaks.add(controls, "minDistance").min(0).max(20).step(1).name("Min Distance");
cameraTweaks.add(controls, "maxDistance").min(10).max(40).step(1).name("Max Distance");

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
  powerPreference: "high-performance",
  stencil: false,
  depth: false,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Post-processing
 */
const renderScene = new RenderPass(scene, camera);

const bloomPass = new UnrealBloomPass(
  new THREE.Vector2(sizes.width, sizes.height),
  1.5, // Strength
  0.4, // Radius
  0.85 // Threshold
);

const composer = new EffectComposer(renderer);
composer.addPass(renderScene);
composer.addPass(bloomPass);

/**
 * Animate
 */
const raycaster = new THREE.Raycaster();

function randomizeIntensity(baseValue, variation) {
  return baseValue + Math.random() * variation;
}

const clock = new THREE.Clock();
let time = Date.now();
const tick = () => {
  const elapsedTime = clock.getElapsedTime();
  const currentTime = Date.now();
  const deltaTime = currentTime - time;
  time = currentTime;

  dustMaterial.uniforms.uTime.value = elapsedTime;

  // Update controls
  controls.update();

  if (sceneReady) {
    for (const point of points) {
      const screenPosition = point.position.clone();
      screenPosition.project(camera);

      raycaster.setFromCamera(screenPosition, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length === 0 && debugObject.poi) {
        point.element.classList.add("visible");
      } else {
        const intersectionDistance = intersects[0].distance;
        const pointDistance = point.position.distanceTo(camera.position);

        if (intersectionDistance > pointDistance) {
          point.element.classList.remove("visible");
        } else if (intersectionDistance < pointDistance && debugObject.poi) {
          point.element.classList.add("visible");
        }
      }

      const translateX = screenPosition.x * sizes.width * 0.5;
      const translateY = -screenPosition.y * sizes.height * 0.5;
      point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
    }
  }

  if (mixer) {
    mixer.update(deltaTime * 0.001);
  }

  // Make Skull_Gravitti follow the camera
  if (skullGraffitiMesh) {
    const cameraDirection = new THREE.Vector3();
    camera.getWorldDirection(cameraDirection);
    // skullGraffitiMesh.lookAt(camera.position);
    // skullGraffitiMesh.quaternion.copy(camera.quaternion);
    skullGraffitiMesh.lookAt(camera.position.x - 5, -6, camera.position.z);
  }

  FireEmmision.emissiveIntensity = randomizeIntensity(2, 2.5);
  FireEmmision2.emissiveIntensity = randomizeIntensity(2.5, 2);
  FireEmmision3.emissiveIntensity = randomizeIntensity(3, 2);
  FireEmmision4.emissiveIntensity = randomizeIntensity(2, 2.5);
  FireEmmision5.emissiveIntensity = randomizeIntensity(1.5, 2.5);
  FireEmmision6.emissiveIntensity = randomizeIntensity(3.5, 2);
  FireEmmision7.emissiveIntensity = randomizeIntensity(2.5, 1.5);
  FireEmmision8.emissiveIntensity = randomizeIntensity(2, 2);
  FireEmmision9.emissiveIntensity = randomizeIntensity(1.5, 2.5);
  FireEmmision10.emissiveIntensity = randomizeIntensity(3, 2);
  FireEmmision11.emissiveIntensity = randomizeIntensity(2.5, 2);

  // Render scene with bloom
  composer.render();

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
